/**
 * @CODE:SPEC-ACC-002-PAGINATION-001
 * PaginationControls Component
 *
 * Reusable pagination controls extracted from AccommodationSearch
 * - Previous/Next buttons
 * - Page number buttons with ellipsis
 * - Page info display
 *
 * @performance
 * - Memoized for preventing unnecessary re-renders
 * - Optimized page number calculation
 */

import { memo, useMemo } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

interface PaginationControlsProps {
  currentPage: number;
  totalPages: number;
  itemsPerPage: number;
  total: number;
  onPageChange: (page: number) => void;
}

const PaginationControls = memo(
  ({
    currentPage,
    totalPages,
    itemsPerPage,
    total,
    onPageChange,
  }: PaginationControlsProps) => {
    // Calculate visible page numbers
    const visiblePageNumbers = useMemo(() => {
      const pageNumbers = [];
      const maxPages = 5;

      if (totalPages <= maxPages) {
        for (let i = 1; i <= totalPages; i++) {
          pageNumbers.push(i);
        }
      } else if (currentPage <= 3) {
        for (let i = 1; i <= maxPages; i++) {
          pageNumbers.push(i);
        }
      } else if (currentPage >= totalPages - 2) {
        for (let i = totalPages - 4; i <= totalPages; i++) {
          pageNumbers.push(i);
        }
      } else {
        for (let i = currentPage - 2; i <= currentPage + 2; i++) {
          pageNumbers.push(i);
        }
      }

      return pageNumbers;
    }, [currentPage, totalPages]);

    // Calculate item range for display
    const startItem = (currentPage - 1) * itemsPerPage + 1;
    const endItem = Math.min(currentPage * itemsPerPage, total);

    const handlePrevClick = () => {
      if (currentPage > 1) {
        onPageChange(currentPage - 1);
      }
    };

    const handleNextClick = () => {
      if (currentPage < totalPages) {
        onPageChange(currentPage + 1);
      }
    };

    return (
      <div className="mt-12 flex flex-col sm:flex-row items-center justify-between gap-4">
        {/* Item range display */}
        <div className="text-sm text-gray-600">
          {startItem}-{endItem} / {total}개
        </div>

        {/* Pagination buttons */}
        <div className="flex items-center gap-2">
          {/* Previous button */}
          <button
            type="button"
            onClick={handlePrevClick}
            disabled={currentPage === 1}
            className={`flex items-center gap-1 px-3 py-2 text-sm rounded-lg transition-colors ${
              currentPage === 1
                ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                : "bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
            }`}
            aria-label="이전 페이지"
          >
            <ChevronLeft className="w-4 h-4" />
            이전
          </button>

          {/* Page number buttons */}
          <div className="flex items-center gap-1">
            {visiblePageNumbers.map((pageNum) => (
              <button
                type="button"
                key={pageNum}
                onClick={() => onPageChange(pageNum)}
                className={`w-8 h-8 text-sm rounded-lg transition-colors ${
                  currentPage === pageNum
                    ? "bg-[#F39800] text-white"
                    : "bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
                }`}
                aria-label={`${pageNum} 페이지`}
                aria-current={currentPage === pageNum ? "page" : undefined}
              >
                {pageNum}
              </button>
            ))}

            {/* Ellipsis for skipped pages */}
            {totalPages > 5 && currentPage < totalPages - 2 && (
              <>
                <span className="text-gray-400 px-2">...</span>
                <button
                  type="button"
                  onClick={() => onPageChange(totalPages)}
                  className="w-8 h-8 text-sm rounded-lg bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
                  aria-label={`${totalPages} 페이지`}
                >
                  {totalPages}
                </button>
              </>
            )}
          </div>

          {/* Next button */}
          <button
            type="button"
            onClick={handleNextClick}
            disabled={currentPage === totalPages}
            className={`flex items-center gap-1 px-3 py-2 text-sm rounded-lg transition-colors ${
              currentPage === totalPages
                ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                : "bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
            }`}
            aria-label="다음 페이지"
          >
            다음
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>

        {/* Page info display */}
        <div className="text-sm text-gray-600">
          {currentPage} / {totalPages} 페이지
        </div>
      </div>
    );
  }
);

PaginationControls.displayName = "PaginationControls";

export default PaginationControls;
